import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { api } from "@/lib/api";
import { useQuery, invalidate } from "@/lib/useQuery";
import type { Ticket } from "@/lib/types";
import { ago, shortDate, titleCase } from "@/lib/format";
import { PageHeader, Loading, Empty, Dot, Card, Button, Modal, Field, Tag, AiTag, toast } from "@/components/ui";

export function TicketsPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [state, setState] = useState<"open" | "closed" | "all">("open");
  const accountId = params.get("account") ?? "";
  const key = `tickets:${state}:${accountId}`;
  const { data, loading, refetch } = useQuery<Ticket[]>(key, () => api.get<Ticket[]>(`/tickets?state=${state}${accountId ? `&accountId=${accountId}` : ""}`), [state, accountId]);
  return (
    <>
      <PageHeader title="Tickets" sub={data ? `${data.length}` : undefined}>
        <div className="seg">{(["open", "closed", "all"] as const).map((s) => <button key={s} className={state === s ? "active" : ""} onClick={() => setState(s)}>{titleCase(s)}</button>)}</div>
      </PageHeader>
      <div className="page">
        <TicketTable tickets={data} loading={loading} onOpen={(t) => nav(`/tickets/${t.id}`)} showAccount />
      </div>
      {id && <TicketModal id={id} onClose={() => nav("/tickets")} onChanged={() => { refetch(); invalidate("badges"); invalidate("systems"); }} />}
    </>
  );
}

export function TicketTable({ tickets, loading, onOpen, showAccount }: { tickets: Ticket[] | undefined; loading: boolean; onOpen: (t: Ticket) => void; showAccount?: boolean }) {
  return (
    <Card className="table-wrap">
      {loading && !tickets ? <Loading /> : (tickets ?? []).length === 0 ? <Empty>No tickets.</Empty> : (
        <table className="table">
          <thead><tr><th>ID</th><th>Title</th>{showAccount && <th>Account</th>}<th>Billing</th><th>Issue</th><th>State</th><th>Age</th></tr></thead>
          <tbody>
            {(tickets ?? []).map((t) => (
              <tr key={t.id} className="click" onClick={() => onOpen(t)}>
                <td className="num muted">T-{t.number}</td>
                <td><span className="row gap-8"><Dot color={t.state === "closed" ? null : t.type === "incident" ? "red" : "yellow"} />{t.title}{t.aiGenerated && <AiTag />}</span></td>
                {showAccount && <td className="muted-2">{t.accountName}</td>}
                <td><span className="pill" style={t.billingTag === "quote_it" ? { color: "var(--status-yellow-text)" } : undefined}>{t.billingTag === "license" ? "License" : "Quote it"}</span></td>
                <td>{t.githubIssueUrl ? <a href={t.githubIssueUrl} target="_blank" rel="noreferrer" className="mono small" onClick={(e) => e.stopPropagation()}>#{t.githubIssueNumber}</a> : <span className="dim">—</span>}</td>
                <td className="muted-2">{titleCase(t.state)}</td>
                <td className="num muted">{ago(t.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

interface TicketDetail extends Ticket { repo: string | null; sourceThreadId: string | null; assigneeName: string | null }

export function TicketModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const { data: t, refetch } = useQuery<TicketDetail>(`ticket:${id}`, () => api.get<TicketDetail>(`/tickets/${id}`), [id]);
  const [busy, setBusy] = useState(false);
  if (!t) return <Modal title="Ticket" onClose={onClose}><Loading /></Modal>;
  const patch = async (body: Record<string, unknown>) => { setBusy(true); try { await api.patch(`/tickets/${t.id}`, body); refetch(); onChanged(); } catch (e) { toast((e as Error).message, true); } finally { setBusy(false); } };
  return (
    <Modal title={<span className="row gap-8"><span className="mono muted">T-{t.number}</span> {t.title}{t.aiGenerated && <AiTag />}</span>} onClose={onClose} wide footer={<>
      {t.state !== "closed" ? <Button variant="primary" disabled={busy} onClick={() => patch({ state: "closed" })}>Close ticket{t.githubIssueNumber ? " and issue" : ""}</Button> : <Button disabled={busy} onClick={() => patch({ state: "open" })}>Reopen</Button>}
    </>}>
      <div className="row wrap gap-8">
        <Tag color={t.type === "incident" ? "red" : "yellow"}>{t.type === "incident" ? "Incident" : "Change request"}</Tag>
        <Tag color="outline">{titleCase(t.severity)}</Tag>
        <Tag color="outline">{titleCase(t.state)}</Tag>
        <Link to={`/accounts/${t.accountId}`} className="pill accent">{t.accountName}</Link>
        {t.systemName && <span className="pill">{t.systemName}</span>}
        {t.githubIssueUrl && <a className="pill" href={t.githubIssueUrl} target="_blank" rel="noreferrer">GitHub #{t.githubIssueNumber}</a>}
        {t.sourceThreadId && <Link to={`/inbox/${t.sourceThreadId}`} className="pill">Source email</Link>}
      </div>
      {t.summary && <div className="prose" style={{ whiteSpace: "pre-wrap" }}>{t.summary}</div>}
      <div className="form-grid">
        <Field label="Billing" hint="License: covered by the monthly license. Quote it: scoped and billed separately.">
          <select className="select" value={t.billingTag} disabled={busy} onChange={(e) => patch({ billingTag: e.target.value })}><option value="license">License</option><option value="quote_it">Quote it</option></select>
        </Field>
        <Field label="Type"><select className="select" value={t.type} disabled={busy} onChange={(e) => patch({ type: e.target.value })}><option value="incident">Incident</option><option value="change">Change request</option></select></Field>
        <Field label="Severity"><select className="select" value={t.severity} disabled={busy} onChange={(e) => patch({ severity: e.target.value })}>{["low", "normal", "high", "critical"].map((s) => <option key={s} value={s}>{titleCase(s)}</option>)}</select></Field>
        <Field label="State"><select className="select" value={t.state} disabled={busy} onChange={(e) => patch({ state: e.target.value })}><option value="open">Open</option><option value="in_progress">In progress</option><option value="closed">Closed</option></select></Field>
      </div>
      <span className="small muted">Opened {shortDate(t.createdAt)}{t.closedAt ? ` · closed ${shortDate(t.closedAt)}` : ""}</span>
    </Modal>
  );
}
